import type { FinPayrollBatchRow } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Table } from '@shared/ui/Table';
import { formatDateRu, formatMoney } from '../financierFormat';
import {
  TableWrap,
  RightTh,
  RightTd,
  TableNoticeTd,
  BoldTitle,
  GreenProjectText,
  MutedText,
  PeriodText,
  BatchTypeText,
  NdflDeduction,
  CanteenDeduction,
  NetPayout,
  DiscrepancyBadge,
  SubExplanationText,
  InRegistryText,
} from './styled';
import { BATCH_TYPE_LABELS, BATCH_STATUS_CONFIG } from './types';
import { batchAction, monthLabel, monthTitle } from './model';

interface PayrollBatchesTableProps {
  batches: FinPayrollBatchRow[];
  /** Месяц сводки (первое число). */
  periodMonth: string;
  onEnqueue: (batchId: string) => void;
  isEnqueuing: boolean;
  /** Ведомость, которая передаётся сейчас. */
  enqueuingId: string | null;
  canWrite: boolean;
}

export function PayrollBatchesTable({
  batches,
  periodMonth,
  onEnqueue,
  isEnqueuing,
  enqueuingId,
  canWrite,
}: PayrollBatchesTableProps) {
  return (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            <th>Ведомость / Объект</th>
            <th>Период / Дата выплаты</th>
            <th>Тип выплаты</th>
            <th>Работников / Смен</th>
            <RightTh>Начислено / Удержания</RightTh>
            <RightTh>К выплате, ₽</RightTh>
            <th>Сверка с рапортами</th>
            <th>Статус</th>
            <RightTh>Действия</RightTh>
          </tr>
        </thead>
        <tbody>
          {batches.length === 0 ? (
            <tr>
              <TableNoticeTd colSpan={9}>
                Зарплатных ведомостей за {monthLabel(periodMonth)} нет
              </TableNoticeTd>
            </tr>
          ) : (
            batches.map((b: FinPayrollBatchRow) => {
              const statusConf = BATCH_STATUS_CONFIG[b.status] ?? {
                label: b.status,
                tone: 'neutral',
              };
              // Статус — по факту заявки; право записи влияет только на кнопку
              const action = batchAction(b, canWrite);

              return (
                <tr key={b.id}>
                  <td>
                    <BoldTitle>{b.title}</BoldTitle>
                    <GreenProjectText>
                      {b.project_name}
                    </GreenProjectText>
                    <MutedText>
                      Банк: {b.salary_bank_name}
                    </MutedText>
                  </td>
                  <td>
                    <PeriodText>{monthTitle(b.period_month)}</PeriodText>
                    <MutedText>
                      Выплата: {formatDateRu(b.payout_date)}
                    </MutedText>
                  </td>
                  <td>
                    <BatchTypeText>
                      {BATCH_TYPE_LABELS[b.batch_type] ?? b.batch_type}
                    </BatchTypeText>
                  </td>
                  <td>
                    <div><b>{b.employees_count}</b> чел.</div>
                    <MutedText>
                      {b.shifts_verified_count} смен
                    </MutedText>
                  </td>
                  <RightTd>
                    <div>{formatMoney(b.gross_accrued_amount)}</div>
                    <NdflDeduction>
                      НДФЛ: −{formatMoney(b.ndfl_amount)}
                    </NdflDeduction>
                    {b.canteen_deduction_amount > 0 && (
                      <CanteenDeduction>
                        Питание: −{formatMoney(b.canteen_deduction_amount)}
                      </CanteenDeduction>
                    )}
                  </RightTd>
                  <RightTd>
                    <NetPayout>
                      {formatMoney(b.net_payout_amount)}
                    </NetPayout>
                  </RightTd>
                  <td>
                    <DiscrepancyBadge $isOk={b.field_discrepancy_pct <= 2}>
                      {b.field_discrepancy_pct.toFixed(1)}% расхождение
                    </DiscrepancyBadge>
                    <SubExplanationText>
                      с рапортами прорабов
                    </SubExplanationText>
                  </td>
                  <td>
                    <Badge $tone={statusConf.tone}>{statusConf.label}</Badge>
                  </td>
                  <RightTd>
                    {action === 'enqueue' ? (
                      <Button
                        $variant="primary"
                        $size="sm"
                        disabled={isEnqueuing}
                        onClick={() => onEnqueue(b.id)}
                      >
                        {enqueuingId === b.id ? 'Передача…' : 'В оплату (Гендиректор)'}
                      </Button>
                    ) : action === 'in_registry' ? (
                      <InRegistryText>В реестре</InRegistryText>
                    ) : action === 'not_enqueued' ? (
                      <InRegistryText>Не передана в оплату</InRegistryText>
                    ) : (
                      <InRegistryText>—</InRegistryText>
                    )}
                  </RightTd>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </TableWrap>
  );
}
